import { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { PanelMenu } from "primereact/panelmenu";

import { User } from "../../../contexts/User/types";
import { INTERNAL_ROUTES, EXTERNAL_ROUTES } from "../../../../constants/routes";
import { removeCurrentSelectedTenant, signOut } from "../../../../utils";
import { NavUserImage } from "../NavUserImage/NavUserImage";
import { NavContact } from "../NavContact/NavContact";
import { ThemeSwitcherMenuItem } from "./ThemeSwitcherMenuItem";
import { CodeEditorThemeMenuItem } from "./CodeEditorThemeMenuItem";
import { TourToggleMenuItem } from "./TourToggleMenuItem";
import { fetchAvailableTenants, type AvailableTenant } from "../../../../api/availableTenants";

import S from "../UserNavBar.module.scss";

interface ExtendedMenuItem extends Omit<import("primereact/menuitem").MenuItem, "items"> {
  key?: string;
  openInNewTab?: boolean;
  onClick?: () => void;
  items?: ExtendedMenuItem[];
}

interface ProfileMenuProps {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTourVisible: boolean;
  toggleTourVisibility: () => void;
  navigateTo: (path: string, openInNewTab?: boolean) => void;
  setUser: (user: User | null) => void;
  selectedTenant: string | null;
  isSjAdmin: boolean;
  impersonatedEmail: string | null;
  sjAdminLeaveTenant: () => void;
  sjAdminStopImpersonation: () => void;
  contacts?: string | null;
}

export function ProfileMenu({
  user,
  isAuthenticated,
  isLoading,
  isTourVisible,
  toggleTourVisibility,
  navigateTo,
  selectedTenant,
  isSjAdmin,
  impersonatedEmail,
  sjAdminLeaveTenant,
  sjAdminStopImpersonation,
  contacts,
}: ProfileMenuProps) {
  const op = useRef<OverlayPanel>(null);
  const profileButtonRef = useRef<Button>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isContactVisible, setIsContactVisible] = useState(false);
  const [availableTenants, setAvailableTenants] = useState<AvailableTenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({
    account: false,
    ui_settings: false,
    help_support: false,
  });

  useEffect(() => {
    const loadTenants = async () => {
      if (!user || (user?.tenants?.length ?? 0) <= 1) return;

      setIsLoadingTenants(true);
      try {
        const tenants = await fetchAvailableTenants();
        setAvailableTenants(tenants);
      } catch (error) {
        console.error("Failed to fetch available tenants:", error);
      } finally {
        setIsLoadingTenants(false);
      }
    };

    loadTenants();
  }, [user]);

  const logOut = async () => {
    if (isSjAdmin && impersonatedEmail) {
      await sjAdminStopImpersonation();
      window.location.assign(INTERNAL_ROUTES.Admin.routes.users.route);
    } else {
      await signOut();
    }
  };

  const handleChangeTenant = async () => {
    if (isSjAdmin) {
      await sjAdminLeaveTenant();
    } else {
      removeCurrentSelectedTenant();
      window.location.assign(INTERNAL_ROUTES.Login.routes.tenantSelection.route);
    }
  };

  const currentTenantDisplayName = () => {
    if (!selectedTenant || !availableTenants.length) return selectedTenant;
    const currentTenant = availableTenants.find(t => t.name === selectedTenant);
    return currentTenant?.displayName || selectedTenant;
  };

  const profileItems: ExtendedMenuItem[] = [
    {
      key: "account",
      label: "My Account",
      icon: "pi pi-user",
      visible: !!user,
      items: [
        {
          label: INTERNAL_ROUTES.User.routes.achievements.name,
          command: () => navigateTo(INTERNAL_ROUTES.User.routes.achievements.route),
        },
        {
          label: INTERNAL_ROUTES.User.routes.certificates.name,
          command: () => navigateTo(INTERNAL_ROUTES.User.routes.certificates.route),
        },
        {
          label: INTERNAL_ROUTES.User.routes.history.name,
          command: () => navigateTo(INTERNAL_ROUTES.User.routes.history.route),
        },
        {
          label: INTERNAL_ROUTES.User.routes.notes.name,
          command: () => navigateTo(INTERNAL_ROUTES.User.routes.notes.route),
        },
        {
          label: INTERNAL_ROUTES.User.routes.profile.name,
          command: () => navigateTo(INTERNAL_ROUTES.User.routes.profile.route),
        },
        {
          label: INTERNAL_ROUTES.User.routes.settings.name,
          command: () => navigateTo(INTERNAL_ROUTES.User.routes.settings.route),
        },
      ],
    },
    {
      key: "ui_settings",
      label: "UI Settings",
      icon: "pi pi-cog",
      items: [
        {
          label: "Theme",
          template: () => <ThemeSwitcherMenuItem />,
        },
        {
          label: "Code Editor Theme",
          template: () => <CodeEditorThemeMenuItem />,
        },
        {
          label: "Tour",
          template: () => (
            <TourToggleMenuItem
              isTourVisible={isTourVisible}
              toggleTourVisibility={toggleTourVisibility}
            />
          ),
        },
      ],
    },
    {
      key: "help_support",
      label: "Help & Support",
      icon: "pi pi-compass",
      items: [
        {
          label: "Chat with Support",
          command: () => {
            window.HubSpotConversations?.widget.open();
          },
        },
        {
          label: "Knowledge Base",
          command: () => navigateTo(EXTERNAL_ROUTES.knowledgeBase, true),
        },
        {
          label: "Provide Feedback",
          command: () => navigateTo(INTERNAL_ROUTES.Learn.routes.feedback.route),
        },
        {
          label: "Contact Your Security Team",
          visible: Boolean(contacts),
          command: () => setIsContactVisible(true),
        },
      ],
    },
    {
      key: "logout",
      id: "logout",
      visible: isAuthenticated,
      label: isSjAdmin && impersonatedEmail ? "Stop Impersonating" : "Logout",
      command: () => logOut(),
      icon: isSjAdmin && impersonatedEmail ? "pi pi-user-minus" : "pi pi-sign-out",
    },
  ];

  const handleToggle = (e: React.SyntheticEvent) => {
    op.current?.toggle(e);
    setIsPanelOpen(prev => !prev);
  };

  const handleClose = useCallback(() => {
    op.current?.hide();
    setIsPanelOpen(false);
    // PrimeReact's Button typings don't expose getElement, but the instance has it
    (profileButtonRef.current as { getElement?: () => HTMLElement } | null)
      ?.getElement?.()
      ?.focus();
  }, []);

  // Close overlay on Escape and return focus to profile button
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isPanelOpen, handleClose]);

  return (
    <>
      <Button
        ref={profileButtonRef}
        id="profile-button"
        data-testid="profile-button"
        className="cursor-pointer"
        unstyled
        onClick={handleToggle}
        aria-label="User menu"
        aria-haspopup="true"
        aria-expanded={isPanelOpen}
        style={{ backgroundColor: "transparent", display: "inline-flex", alignItems: "center" }}
      >
        <NavUserImage user={user ?? undefined} isLoading={isLoading} />
      </Button>

      <OverlayPanel
        ref={op}
        id="profile-overlay"
        showCloseIcon={false}
        className={S.profileOverlay}
        onHide={() => setIsPanelOpen(false)}
        aria-label="User menu"
        pt={{
          root: {
            role: "region",
            "aria-label": "User menu",
          },
        }}
      >
        {(user?.tenants?.length ?? 0) > 1 && (
          <div className="flex flex-col p-3 border-b body-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-aaa">Tenant:</span>
              <span className="font-medium truncate flex-1">{currentTenantDisplayName()}</span>
              <Button
                label={isSjAdmin ? "Leave Tenant" : "Change"}
                size="small"
                className={S.changeTenantButton}
                text
                onClick={handleChangeTenant}
                disabled={isLoadingTenants}
              />
            </div>
          </div>
        )}
        <PanelMenu
          className="mt-4"
          model={profileItems}
          expandedKeys={expandedKeys}
          onExpandedKeysChange={setExpandedKeys}
          pt={{
            // PrimeReact's pt typings declare `props` as PanelMenuProps, but at runtime
            // the header callback receives the per-panel props carrying the menu item
            header: ({ props }: { props?: { item?: ExtendedMenuItem } }) => {
              // Items without sub-items (e.g. Logout) have no content panel to control,
              // so we remove the aria-controls attribute to avoid confusion for screen readers
              const hasSubItems = (props?.item?.items?.length ?? 0) > 0;
              return hasSubItems ? {} : { "aria-controls": null };
            },
            headerAction: {
              href: null,
            },
          }}
        />
      </OverlayPanel>

      <NavContact
        isVisible={isContactVisible}
        onVisibilityChange={v => setIsContactVisible(v)}
        contacts={contacts}
      />
    </>
  );
}
